import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import Papa from 'papaparse';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingValues } from '@/features/settings/hooks/use-setting-values';

import { getItemTemplateSchemas } from '../api';
import {
  buildItemTemplateCsvExport,
  collectAllItemTemplates,
} from '../utils/item-template-csv-export';

const PAGE_LIMIT = 100;
const FILENAME_PREFIX = 'item_templates';

const downloadCsv = (filename: string, headers: string[], rows: string[][]) => {
  const csvBody = Papa.unparse({ fields: headers, data: rows });
  // Prepend BOM so Excel reads UTF-8 (incl. Japanese) correctly.
  const csv = '\ufeff' + csvBody;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const ItemTemplateCsvExport = () => {
  const { t } = useTranslation();
  const { orgId } = useScopeIds('org');
  const notifications = useNotifications();
  const { getSettingEntityValue, isLoading: isLoadingSettings } = useSettingValues();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    // Settings back the id→name resolution; exporting before they load would
    // emit raw ids and break the round-trip with import.
    if (!orgId || isLoadingSettings) return;
    setIsExporting(true);
    try {
      const templates = await collectAllItemTemplates((cursor) =>
        getItemTemplateSchemas(orgId, { cursor, limit: PAGE_LIMIT }),
      );

      if (templates.length === 0) {
        notifications.info('itemTemplates.csvExport.empty');
        return;
      }

      const resolveSettingName = (id: string) => getSettingEntityValue<string>(id, id);
      const { headers, rows } = buildItemTemplateCsvExport(templates, resolveSettingName, t);
      downloadCsv(`${FILENAME_PREFIX}_${Date.now()}`, headers, rows);

      notifications.success('itemTemplates.csvExport.success');
    } catch (error) {
      console.error(error);
      notifications.error('itemTemplates.csvExport.error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      startIcon={<DownloadIcon />}
      loading={isExporting}
      disabled={isLoadingSettings}
      onClick={() => void handleExport()}
    >
      {t('itemTemplates.csvExport.button')}
    </Button>
  );
};
